import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  MenuItem,
  Grid,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";

import Autocomplete from "@mui/material/Autocomplete";

import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);

const API_URL = "https://api.hr-ebnhaian.cloud/api/v1/doctors";
const API_BASE_URL = "https://api.hr-ebnhaian.cloud/api/v1";

const nationalityOptions = Object.values(
  countries.getNames("en", { select: "official" })
);

function DoctorEdit() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const section = params.get("section") || "basic";
  const navigate = useNavigate();

  const [doctor, setDoctor] = useState(null);
  const [centers, setCenters] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    fatherName: "",
    phone: "",
    email: "",
    address: "",
    center: "",
    department: "",
    specialization: "",
    nationality: "",
    salary: {
      basicSalary: "",
    },
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [doctorRes, centersRes, depRes] = await Promise.all([
          axios.get(`${API_URL}/${id}`),
          axios.get(`${API_BASE_URL}/centers`),
          axios.get(`${API_BASE_URL}/departments`),
        ]);

        const d = doctorRes.data?.data || {};

        setDoctor(d);
        setCenters(centersRes.data?.data || []);
        setDepartments(depRes.data?.data || []);

        setForm({
          firstName: d.firstName || "",
          lastName: d.lastName || "",
          fatherName: d.fatherName || "",
          phone: d.phone || "",
          email: d.email || "",
          address: d.address || "",

          center:
            typeof d.center === "object"
              ? d.center?._id || ""
              : d.center || "",

          department:
            typeof d.department === "object"
              ? d.department?._id || ""
              : d.department || "",

          specialization: d.specialization || "",
          nationality: d.nationality || "",

          salary: {
            basicSalary: d.salary?.basicSalary ?? "",
          },
        });
      } catch (err) {
        console.log("LOAD ERROR:", err);
      }
    };

    loadData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("salary.")) {
      const key = name.split(".")[1];

      setForm((prev) => ({
        ...prev,
        salary: {
          ...prev.salary,
          [key]: value,
        },
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {

      console.log("======= FORM BEFORE SEND =======");
      console.log(form);

      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        fatherName: form.fatherName,
        phone: form.phone,
        email: form.email,
        address: form.address,
        specialization: form.specialization,
        nationality: form.nationality,

        center:
          typeof form.center === "object"
            ? form.center._id
            : form.center,

        department:
          typeof form.department === "object"
            ? form.department._id
            : form.department,

        salary: {
          basicSalary:
            form.salary?.basicSalary !== ""
              ? Number(form.salary.basicSalary)
              : "",
        },
      };

      console.log("======= PAYLOAD SENT =======");
      console.log(payload);

      console.log("CENTER VALUE:", payload.center);
      console.log("DEPARTMENT VALUE:", payload.department);

      await axios.put(`${API_URL}/${id}`, payload);

      setSnack({
        open: true,
        message: "Doctor updated successfully",
        severity: "success",
      });

      navigate(`/doctors/${id}`);

    } catch (err) {

      console.log("UPDATE ERROR:", err.response?.data || err);

      setSnack({
        open: true,
        message: "Update failed",
        severity: "error",
      });
    }
  };

  if (!doctor) return "Loading...";

  return (
    <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Edit Doctor – {section}
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 3 }}>

        <Typography fontWeight="bold" mb={2}>
          Doctor Information
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>

          <Grid item xs={12} md={3}>
            <TextField
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Father Name"
              name="fatherName"
              value={form.fatherName}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              options={nationalityOptions}
              value={form.nationality || null}
              onChange={(e, v) =>
                setForm((prev) => ({
                  ...prev,
                  nationality: v || "",
                }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Nationality" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Center"
              name="center"
              value={form.center}
              onChange={handleChange}
              fullWidth
            >
              {centers.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Department"
              name="department"
              value={form.department}
              onChange={handleChange}
              fullWidth
            >
              {departments.map((d) => (
                <MenuItem key={d._id} value={d._id}>
                  {d.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Specialization"
              name="specialization"
              value={form.specialization}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Basic Salary"
              type="number"
              name="salary.basicSalary"
              value={form.salary.basicSalary}
              onChange={handleChange}
              fullWidth
            />
          </Grid>

        </Grid>

        <Stack direction="row" spacing={2} mt={4}>
          <Button variant="contained" color="success" onClick={handleSave}>
            Save
          </Button>

          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Stack>

      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DoctorEdit;